/**
 * 睡眠中の心拍・Body Battery・ストレスの推移
 */

import { useMemo } from 'react'
import { CartesianGrid, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import type { SleepSeries, SleepSeriesPoint } from '@/api/types/sleep'
import { parseUtcDate } from '@/lib/date-utils'

import { formatJstTime } from '../format'

type Props = {
  series: SleepSeries
  /** 塗りつぶす睡眠の時間帯（ISO） */
  ranges: { start: string; end: string }[]
}

type Point = { t: number; v: number }

const toPoints = (points: SleepSeriesPoint[]): Point[] =>
  points.map((p) => ({ t: parseUtcDate(p.t).getTime(), v: p.v }))

const LINES = [
  { key: 'hr', label: '心拍', color: 'hsl(0 75% 55%)', axis: 'hr' },
  { key: 'bb', label: 'Body Battery', color: 'hsl(142 70% 40%)', axis: 'pct' },
  { key: 'stress', label: 'ストレス', color: 'hsl(30 90% 55%)', axis: 'pct' },
] as const

const formatTick = (ms: number) => formatJstTime(new Date(ms).toISOString())

export const VitalsChart = ({ series, ranges }: Props) => {
  const data = useMemo(
    () =>
      new Map<string, Point[]>([
        ['hr', toPoints(series.heart_rate)],
        ['bb', toPoints(series.body_battery)],
        ['stress', toPoints(series.stress)],
      ]),
    [series],
  )
  const areas = ranges.map((r) => ({ x1: parseUtcDate(r.start).getTime(), x2: parseUtcDate(r.end).getTime() }))
  const hasData = [...data.values()].some((points) => points.length > 0)
  if (!hasData) return null

  return (
    <section className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">心拍・Body Battery・ストレス</span>
        {LINES.map((line) => (
          <span key={line.key} className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <span className="inline-block size-2 rounded-full" style={{ backgroundColor: line.color }} />
            {line.label}
          </span>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          {areas.map((a) => (
            <ReferenceArea key={a.x1} x1={a.x1} x2={a.x2} yAxisId="pct" fill="hsl(239 84% 67%)" fillOpacity={0.08} />
          ))}
          <XAxis
            dataKey="t"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatTick}
            tick={{ fontSize: 10 }}
            allowDuplicatedCategory={false}
          />
          <YAxis yAxisId="hr" domain={['dataMin - 5', 'dataMax + 5']} tick={{ fontSize: 10 }} />
          <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} hide />
          <Tooltip
            labelFormatter={(ms) => formatTick(Number(ms))}
            contentStyle={{ backgroundColor: 'var(--popover)', border: '1px solid var(--border)', fontSize: 12 }}
          />
          {LINES.map((line) => (
            <Line
              key={line.key}
              data={data.get(line.key)}
              dataKey="v"
              name={line.label}
              yAxisId={line.axis}
              stroke={line.color}
              dot={false}
              strokeWidth={1.5}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </section>
  )
}
